import React, { useCallback, useEffect, useState } from 'react';
import { Alert, AlertTitle, Box, Button, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { cryptoProService } from '../../services/cryptoProService';
import type { WorkplaceStatus } from '../../models/digitalSignatures/cryptoProTypes';
import { medicsService } from '../../services/medicsService';
import { MedicDigitalSignDto } from '../../models/medics/medicDigitalSignDto';
import { CertificateSelectionDialog } from '../common/CertificateSelectionDialog';
import type { Certificate } from '../../models/digitalSignatures/cryptoProTypes';
import { DateUtils } from '../../helpers/dateUtils';
import { getApiErrorMessage } from '../../helpers/errorUtils';
import { SigningStatus } from '../../models/enums/signingStatus';
import { TokenType } from '../../models/enums/tokenType';
import Spinner from '../common/Spinner';

export const WorkplaceSigningStatus: React.FC = () => {
    const [status, setStatus] = useState<SigningStatus | null>(null);
    const [workplaceStatus, setWorkplaceStatus] = useState<WorkplaceStatus | null>(null);
    const [defaultCert, setDefaultCert] = useState<MedicDigitalSignDto | null>(null);
    const [certificateDialogOpen, setCertificateDialogOpen] = useState(false);
    const [showSpinner, setShowSpinner] = useState(false);

    const setNotConfiguredStatus = useCallback((errorMessage: string) => {
        setWorkplaceStatus({
            isReady: false,
            errors: errorMessage,
        });
        setStatus(SigningStatus.NotConfigured);
        setDefaultCert(null);
    }, []);

    const fetchMedicCertificate = useCallback(async (): Promise<MedicDigitalSignDto | null> => {
        try {
            const cert = await medicsService.getMedicCertificate();
            return cert;
        } catch (error) {
            const message = getApiErrorMessage(error, 'Ошибка проверки статуса подписания');
            setNotConfiguredStatus(message);
            return null;
        }
    }, [setNotConfiguredStatus]);

    const checkSigningStatus = useCallback(async () => {
        try {
            const cert = await fetchMedicCertificate();

            if (cert && cert.tokenType !== TokenType.Physical) {
                setStatus(SigningStatus.Ready);
                setDefaultCert(cert);
                return;
            }

            const wpStatus = await cryptoProService.checkWorkplaceReadiness();
            setWorkplaceStatus(wpStatus);

            if (!wpStatus.isReady) {
                setStatus(SigningStatus.NotConfigured);
                setDefaultCert(null);
                return;
            }

            if (cert) {
                setStatus(SigningStatus.Ready);
                setDefaultCert(cert);
            } else {
                setStatus(SigningStatus.NoCertificate);
                setDefaultCert(null);
            }
        } catch (error) {
            console.error('Ошибка проверки статуса подписания:', error);
            const message = getApiErrorMessage(error, 'Ошибка проверки статуса подписания');
            setNotConfiguredStatus(message);
        }
    }, [fetchMedicCertificate]);

    useEffect(() => {
        checkSigningStatus();
    }, [checkSigningStatus]);

    const handleSelectCertificate = async (certificate: Certificate) => {
        setShowSpinner(true);
        try {
            const certInfo = await cryptoProService.getCertificateInfo(certificate.thumbprint);

            const savedCert = await medicsService.setDefaultCertificate({
                thumbprint: certificate.thumbprint,
                signOwner: certInfo.subject,
                issuedAt: certInfo.validFrom.toISOString(),
                expiredAt: certInfo.validTo.toISOString(),
                serial: certInfo.serialNumber,
                issuedBy: certInfo.issuer,
            });

            setDefaultCert(savedCert);
            setStatus(SigningStatus.Ready);
            setCertificateDialogOpen(false);
        } catch (error: any) {
            console.error('Ошибка сохранения сертификата:', error);
        } finally {
            setShowSpinner(false);
        }
    };

    const handleChangeCertificate = () => {
        setCertificateDialogOpen(true);
    };

    const handleRetryCheck = useCallback(async () => {
        setShowSpinner(true);
        try {
            await checkSigningStatus();
        } finally {
            setShowSpinner(false);
        }
    }, [checkSigningStatus]);

    const renderStatusContent = () => {
        if (status === null) {
            return (
                <Box position="relative" minHeight={80} display="flex" alignItems="center" justifyContent="center">
                    <Spinner text="Проверка ЭЦП" />
                </Box>
            );
        }
        if (showSpinner) {
            return (
                <Box position="relative" minHeight={80} display="flex" alignItems="center" justifyContent="center">
                    <Spinner text="Проверка ЭЦП" />
                </Box>
            );
        }
        if (status === SigningStatus.Ready && defaultCert) {
            return (
                <Alert
                    severity="info"
                    icon={<CheckCircleIcon />}
                    action={
                        <Button color="inherit" size="small" onClick={handleChangeCertificate}>
                            Изменить
                        </Button>
                    }
                >
                    <AlertTitle>ЭЦП для подписи по умолчанию</AlertTitle>
                    {defaultCert.signOwner || 'Сертификат'}, срок действия: {DateUtils.formatDate(defaultCert.expiredAt)}
                </Alert>
            );
        }
        if (status === SigningStatus.NotConfigured) {
            return (
                <Alert
                    severity="error"
                    icon={<ErrorIcon />}
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            onClick={handleRetryCheck}
                            disabled={showSpinner}
                        >
                            Проверить снова
                        </Button>
                    }
                >
                    <AlertTitle>Рабочее место не настроено для подписания документов с помощью ЭЦП</AlertTitle>
                    <Typography variant="body2" component="div" sx={{ mt: 0.5 }}>
                        - Не найдено действующей облачной ЭЦП<br />
                        - Рабочее место не настроено для использования физической ЭЦП
                    </Typography>
                    {workplaceStatus?.errors && (
                        <Typography
                            variant="body2"
                            color="error"
                            sx={{ mt: 1, '& a': { color: 'inherit', textDecoration: 'underline' } }}
                            dangerouslySetInnerHTML={{ __html: workplaceStatus.errors }}
                        />
                    )}
                </Alert>
            );
        }
        if (status === SigningStatus.NoCertificate) {
            return (
                <>
                    <Alert
                        severity="warning"
                        action={
                            <Button color="inherit" size="small" onClick={() => setCertificateDialogOpen(true)}>
                                Выбрать
                            </Button>
                        }
                    >
                        <AlertTitle>Не выбран сертификат для подписи медосмотров</AlertTitle>
                        Выберите сертификат для подписания документов
                    </Alert>
                </>
            );
        }
        return null;
    };

    return (
        <>
            {renderStatusContent()}
            <CertificateSelectionDialog
                open={certificateDialogOpen}
                onClose={() => setCertificateDialogOpen(false)}
                onSelect={handleSelectCertificate}
            />
        </>
    );
};

